import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Button, FormInstance, notification, Row, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';

import { ICongelamentosPorDia, IExtratoCongelamentos } from './interfaces';
import { IRecarga } from '../Recargas/interfaces';

import { EmitirErro } from '../../../../services/EmitirErro';
import { SairDoSistema } from '../../../../services/LStorage/SairSistema';
import { convertDataUSParaBrasil, convertMoedaBRDoJSComAte3CasasDecimais, convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../../services/Conversores';
import { useGetColumnSearchProps } from '../../../../hooks/FilterTable/string';
import ExtratoService from '../../../../services/http/services/CongelamentoPorCNPJ/ExtratoService';
import { formatDateLocal } from './utils';
import { validacaoPermissao, VerificaRota } from '../../../../services/VerificaSePodeAcessar';

export const useExtratoCongelamento = () => {
    const [isCongelamentosModalVisible, setIsCongelamentosModalVisible] = useState(false);
    const [congelamentosModalDate, setCongelamentosModalDate] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [extrato, setExtrato] = useState<IExtratoCongelamentos | null>(null);

    const navigate = useNavigate();

    const refContagemDoFiltroRecargas = useRef<Array<any>>([]);
    const refContagemDoFiltroCongelamentos = useRef<Array<any>>([]);

    const handleOpenCongelamentosModal = useCallback(() => {
        setIsCongelamentosModalVisible(true);
    }, []);

    const handleCloseCongelamentosModal = useCallback(() => {
        setIsCongelamentosModalVisible(false);
    }, []);

    const handleClickViewCongelamentos = useCallback((date: string): void => {
        handleOpenCongelamentosModal();
        setCongelamentosModalDate(date);
    }, [handleOpenCongelamentosModal]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {

                const data = await ExtratoService.getResumo();

                setExtrato(data);

                setIsLoading(false);
            } catch (error) {
                let msgErro: any = (error as Error);
                setIsLoading(false);

                if (msgErro?.message === 'USUáRIO NãO AUTORIZADO.') {
                    SairDoSistema();
                    notification.error({
                        message: 'Sessão expirada',
                        description:
                            'Sua sessão expirou, realize o login novamente!',
                    });
                    navigate('/');
                } else {
                    EmitirErro(error, true, 'respostas_simulador_dkwdfaiweyi')
                }
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [navigate]);

    const columnsRecargas = [
        {
            title: 'Data',
            dataIndex: 'createdAt',
            ...useGetColumnSearchProps('createdAt'),
            width: '2%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (data: any) => convertDataUSParaBrasil(data)
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            ...useGetColumnSearchProps('valor'),
            width: '8%',
            sorter: (a: IRecarga, b: IRecarga) => a.valor - b.valor,
            showSorterTooltip: false,
            render: (valor: number) => {
                return `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valor, true)}`;
            }
        },
        {
            title: 'Saldo restante',
            dataIndex: 'saldoRestante',
            ...useGetColumnSearchProps('saldoRestante'),
            width: '6%',
            sorter: (a: IRecarga, b: IRecarga) => a.saldoRestante - b.saldoRestante,
            showSorterTooltip: false,
            render: (valor: number) => {
                return `R$ ${convertMoedaBRDoJSComAte3CasasDecimais(valor, true)}`;
            }
        },
    ];

    const columnsCongelamentos = [
        {
            title: 'Data',
            dataIndex: 'data',
            ...useGetColumnSearchProps('data'),
            width: '8%',
            sorter: (a: ICongelamentosPorDia, b: ICongelamentosPorDia) => new Date(a.data).getTime() - new Date(b.data).getTime(),
            showSorterTooltip: false,
            render: (valor: string) => {
                return formatDateLocal(valor);
            }
        },
        {
            title: 'Total Congelamentos',
            dataIndex: 'qtdeCongelamentos',
            ...useGetColumnSearchProps('qtdeCongelamentos'),
            width: '6%',
            sorter: (a: ICongelamentosPorDia, b: ICongelamentosPorDia) => a.qtdeCongelamentos - b.qtdeCongelamentos,
            showSorterTooltip: false,
            render: (valor: number) => {
                return valor;
            }
        },
        {
            title: 'Custo Total',
            dataIndex: 'custoTotal',
            ...useGetColumnSearchProps('custoTotal'),
            width: '6%',
            sorter: (a: ICongelamentosPorDia, b: ICongelamentosPorDia) => a.custoTotal - b.custoTotal,
            showSorterTooltip: false,
            render: (valor: number) => {
                return `R$ ${convertMoedaBRDoJSComAte3CasasDecimais(valor, true)}`;
            }
        },

        {
            title: "Visualizar",
            dataIndex: "data",
            width: "1%",
            render: (_: string, record: ICongelamentosPorDia) => (
                <Row>
                    <Tooltip title="Visualizar" color="blue" key="visualizarblue">
                        <Button
                            type="primary"
                            style={{ marginLeft: 5 }}
                            onClick={() => handleClickViewCongelamentos(record.data)}
                        >
                            <SearchOutlined />
                        </Button>
                    </Tooltip>
                </Row>
            )
        }
    ];

    useEffect(() => {
        let arrayUrl = window.location.pathname.split("/");
        VerificaRota(arrayUrl, navigate, true, validacaoPermissao(['manage_config_congelamento_cnpj']));
    }, []);

    return {
        isLoading,
        refContagemDoFiltroRecargas,
        refContagemDoFiltroCongelamentos,
        isResponseModalVisible: isCongelamentosModalVisible,
        handleCloseCongelamentosModal,
        congelamentosModalDate,
        columnsRecargas,
        columnsCongelamentos,
        extrato,
    }
}
